import React, { useState } from 'react';
import { BarChart3, CalendarRange } from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { BillingHistoryView } from './BillingHistoryView';
import { AnnualBillingView } from './AnnualBillingView';
import { clsx } from 'clsx';

type TabId = 'billing-history' | 'annual-billing';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  permission: string;
}

const TABS: Tab[] = [
  { id: 'billing-history', label: 'Histórico de Faturamento', icon: <BarChart3 size={16} />, permission: 'reports.accounting' },
  { id: 'annual-billing', label: 'Faturamento do Ano', icon: <CalendarRange size={16} />, permission: 'reports.accounting' },
];

export const AccountingReportsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabId>('billing-history');

  const availableTabs = TABS.filter(tab => checkPermission(tab.permission).hasPermission);
  const effectiveTab = availableTabs.find(t => t.id === activeTab) ? activeTab : (availableTabs[0]?.id ?? 'billing-history');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Contábil</h1>
        <p className="text-sm text-slate-500 mt-1">Relatórios contábeis e fiscais</p>
      </div>

      {/* Tabs */}
      {availableTabs.length > 1 && (
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px gap-0" aria-label="Abas do relatório contábil">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'inline-flex items-center gap-2 whitespace-nowrap py-3 px-5 text-sm font-medium border-b-2 transition-colors',
                  effectiveTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      {effectiveTab === 'billing-history' && <BillingHistoryView />}
      {effectiveTab === 'annual-billing' && <AnnualBillingView />}
    </div>
  );
};

export default AccountingReportsView;
